import _ from "lodash";
import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import Moment from "react-moment";
import styled from "styled-components";
import { useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { useClient } from "../../../hooks/use-client";
import { Header, Segment, Divider, Image, Button } from "semantic-ui-react";
import { getPublicBulletin, getAppDownloadLinks } from "../../../queries/communications";
import { useMedia } from "react-media";
import { Helmet } from "react-helmet";
import SharePopup from "../components/SharePopup";
import AppSettings from "../../../config";
import { getPublish } from "../../../queries/mobile";
import PlaceholderUntilLoaded from "../../../components/global_components/PlaceholderUntilLoaded";
import Page404 from "../../../components/global_components/Page404";
import classNames from "classnames";
import { useUserAgent } from "../../../hooks/useUserAgent";

import "../components/public_view_preview.scss";
import { useParams } from "react-router";
import { useQueryState } from "nuqs";
import TwoWayCommunicationSection from "../components/public_view/TwoWayCommunicationSection";

import MobileStoreLinks from "../components/MobileStoreLinks";
import useNetworkHeaders from "../../../hooks/useNetworkHeaders";

const StyledReactMarkdown = styled(ReactMarkdown)`
  img {
    max-width: 100%;
    display: block;
    margin: auto;
  }
`;

export const CommunicationsPublicView = (props: any) => {
  const dispatch = useDispatch();
  const publishQuery = useQuery(["mobilePublish"], getPublish(dispatch));
  const {
    params: { id: bulletinId },
  } = props.match;

  const { isGoMobile } = useUserAgent();

  const { site, id } = useParams<{ site: string; id: string }>();
  const networkHeaders = useNetworkHeaders();
  const bulletinQuery = useQuery(["bulletins.public", bulletinId], getPublicBulletin(networkHeaders));
  const downloadLinksQuery = useQuery(["mobileDownloadLinks", site], getAppDownloadLinks(networkHeaders));

  const [askQuestionModalOpen, setAskQuestionModalOpen] = useQueryState<boolean>("askQuestionModalOpen", {
    parse: (value) => Boolean(value),
    defaultValue: false,
  });

  const client = useClient(site);
  const cityName = useMemo(() => _.get(client.query, "data.payload.data.attributes.name", ""), [client.query.data]);
  const citySeal = useMemo(() => _.get(client.query, "data.payload.data.attributes.seal", ""), [client.query.data]);
  const expiredAt = useMemo(() => _.get(bulletinQuery.data, "data.attributes.expired-at", ""), [bulletinQuery.data]);
  const headline = useMemo(() => _.get(bulletinQuery.data, "data.attributes.headline", ""), [bulletinQuery.data]);
  const content = useMemo(() => _.get(bulletinQuery.data, "data.attributes.content", ""), [bulletinQuery.data]);
  const preview = useMemo(() => _.get(bulletinQuery.data, "data.attributes.preview", ""), [bulletinQuery.data]);

  const classification = useMemo<{ classification_id: number; classification_text: string; active: boolean } | null>(
    () => _.get(bulletinQuery.data, "data.attributes.classification", null),
    [bulletinQuery.data],
  );

  const publishedAt = useMemo(
    () => _.get(bulletinQuery.data, "data.attributes.published-at", ""),
    [bulletinQuery.data],
  );
  const isSmallScreen = useMedia({ query: "(max-width: 420px)" });

  const appleStoreUrl = useMemo(() => _.get(downloadLinksQuery.data, "appleLink"), [bulletinQuery.data]);
  const androidUrl = useMemo(() => _.get(downloadLinksQuery.data, "googleLink"), [bulletinQuery.data]);

  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const isLoading = bulletinQuery.isLoading || publishQuery.isLoading || downloadLinksQuery.isLoading;

  if (!isLoading && (!bulletinQuery.data || expiredAt)) {
    return <Page404></Page404>;
  }

  document.title = headline;

  // Function to use native mobile sharing, if on mobile, or to open the custom share menu, if on desktop
  const handleMobileShare = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    // If navigator.share is available (typically on mobile devices)
    if (navigator.share) {
      try {
        await navigator.share({
          url: `${AppSettings.NOTIFICATIONS_API_URL}/public/share/${site}/${id}`,
        });
      } catch (error) {
        console.error("Error sharing:");
      }
    } else {
      // If navigator.share is not available (typically on desktop browsers)
      setIsSharePopupOpen(true);
    }
  };

  return (
    <>
      <Helmet>
        <meta name='description' content={preview} />
        <meta property='og:title' content={headline} />
        <meta property='og:description' content={preview || ""} />
        <meta property='og:image' content={citySeal} />
        <meta name='twitter:card' content='summary'></meta>
      </Helmet>
      <div className='tw-flex tw-min-h-full tw-w-full tw-items-center tw-justify-center'>
        <Segment className='tw-mx-3 tw-my-12 tw-w-full tw-max-w-2xl tw-space-y-6 tw-p-6 tw-shadow-lg'>
          <PlaceholderUntilLoaded isLoading={isLoading} lineCount={1}>
            <Header as='h3' className='unBold'>
              <Image src={citySeal} size='massive' />
              <Header.Content>{cityName}</Header.Content>
            </Header>
          </PlaceholderUntilLoaded>
          <div>
            <PlaceholderUntilLoaded isLoading={isLoading} lineCount={3}>
              <Header className='tw-mb-1' as='h1'>
                <Header.Content
                  className={classNames({ "mobile-word-wrap": isSmallScreen, "desktop-word-wrap": !isSmallScreen })}
                >
                  {headline}
                </Header.Content>
              </Header>
              <Header
                as='h3'
                className={classNames("tw-m-0 tw-font-normal", {
                  "mobile-word-wrap": isSmallScreen,
                  "desktop-word-wrap": !isSmallScreen,
                })}
              >
                {preview}
                <Header.Subheader className='sent-date'>
                  {"Published: "}
                  <Moment format={"MMM D, YYYY @ h:mm A"}>{publishedAt}</Moment>
                </Header.Subheader>
              </Header>
              <SharePopup
                trigger={
                  <Button
                    basic
                    icon='share'
                    content='Share'
                    className='tw-mt-3'
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => handleMobileShare(event)}
                  />
                }
                headline={headline}
                position='bottom left'
                contentUrl={`${AppSettings.NOTIFICATIONS_API_URL}/public/share/${site}/${id}`}
                open={isSharePopupOpen}
                setOpen={setIsSharePopupOpen}
              />
            </PlaceholderUntilLoaded>
            <Divider />
            <PlaceholderUntilLoaded isLoading={isLoading} lineCount={10}>
              <StyledReactMarkdown className={isSmallScreen ? "mobile-word-wrap" : "desktop-word-wrap"}>
                {content}
              </StyledReactMarkdown>
              {classification && (
                <TwoWayCommunicationSection
                  modalOpen={askQuestionModalOpen}
                  headline={headline}
                  classificationName={classification.classification_text}
                  classificationActive={classification.active}
                  openModal={() => setAskQuestionModalOpen(true)}
                  closeModal={() => setAskQuestionModalOpen(false)}
                />
              )}
            </PlaceholderUntilLoaded>
            {!isGoMobile && (
              <>
                {(appleStoreUrl || androidUrl || isLoading) && <Divider />}
                <PlaceholderUntilLoaded isLoading={isLoading} lineCount={1}>
                  <MobileStoreLinks appStoreHref={appleStoreUrl} googlePlayStoreHref={androidUrl} />
                </PlaceholderUntilLoaded>
              </>
            )}
          </div>
        </Segment>
      </div>
    </>
  );
};
